import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { DEMO_NAVIGATION_STEPS, demoEditableValue, demoTutorialStage, matchesDemoObservation, type DemoClientEvent, type DemoNavigationStep, type DemoObservation, type DemoStep, type HostedDemoState } from "@kitsulab/schema";
import { useObservedDemoEditor } from "@/hooks/useDemoEditorObservation";
import { areDraftsEqual } from "@/hooks/useDraftState";

type Demo = NonNullable<HostedDemoState["demo"]>;
function visible(element: Element | null): element is HTMLElement {
	if (!(element instanceof HTMLElement)) return false;
	const box = element.getBoundingClientRect();
	if (!box.width || !box.height || getComputedStyle(element).visibility === "hidden") return false;
	const viewport = element.closest(".react-flow")?.getBoundingClientRect();
	return box.bottom > Math.max(0, viewport?.top ?? 0) && box.top < Math.min(innerHeight, viewport?.bottom ?? innerHeight) && box.right > Math.max(0, viewport?.left ?? 0) && box.left < Math.min(innerWidth, viewport?.right ?? innerWidth);
}
function link(path: string) {
	return Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]")).find(anchor => {
		const pathname = new URL(anchor.href).pathname;
		return visible(anchor) && (pathname === path || pathname === `/app${path === "/" ? "" : path}`);
	}) ?? null;
}

export function HostedDemoTutorial({ demo, active, event, restore, refresh }: {
	demo: Demo;
	active: DemoStep;
	event: (event: DemoClientEvent) => Promise<void>;
	restore: () => void;
	refresh: () => void;
}) {
	const { t } = useTranslation();
	const location = useLocation();
	const editor = useObservedDemoEditor();
	const target = demo.targets[active];
	const progress = demo.progress[active];
	const base = `/projects/${demo.slug}`;
	const collection = active === "shop" ? "recipes" : "items";
	const inProject = location.pathname === base || location.pathname.startsWith(`${base}/`);
	const inCollection = location.pathname === `${base}/${collection}` || location.pathname.startsWith(`${base}/${collection}/`);
	const entityPath = `${base}/${collection}/${target.entityId}`;
	const inEditor = editor?.projectId === demo.projectId && editor.entityId === target.entityId && location.pathname === entityPath;
	const value = useMemo(() => inEditor ? demoEditableValue(editor.draft, active, target.nodeId) : null, [inEditor, editor, active, target.nodeId]);
	const saved = useMemo(() => inEditor ? demoEditableValue(editor.saved, active, target.nodeId) : null, [inEditor, editor, active, target.nodeId]);
	const changed = value !== null && !areDraftsEqual(value, saved);
	const stage = demoTutorialStage(progress, changed, demo.loadedEdits?.[active] ?? null);
	const [error, setError] = useState(false);
	const [missing, setMissing] = useState(false);
	const [away, setAway] = useState(false);
	const [closed, setClosed] = useState(false);
	const [retry, setRetry] = useState(0);
	const valueTracked = useRef(new Set<string>());

	useEffect(() => {
		if (stage !== "save") return;
		const key = `${demo.projectId}:${active}:${progress.editId ?? "first"}`;
		if (valueTracked.current.has(key)) return;
		valueTracked.current.add(key);
		void event({ event: "guided_value_changed", step: active }).catch(() => { valueTracked.current.delete(key); setError(true); });
	}, [stage, active, demo.projectId, progress.editId, event, retry]);

	useEffect(() => {
		let stopped = false;
		let sending = false;
		const outline = document.createElement("div");
		outline.dataset.demoHighlight = "";
		outline.setAttribute("aria-hidden", "true");
		Object.assign(outline.style, {
			position: "fixed", pointerEvents: "none", zIndex: "60", display: "none",
			border: "3px solid #c4b5fd", borderRadius: "8px", boxShadow: "0 0 0 3px #7c3aed88, 0 0 18px #a78bfa66",
		});
		document.body.append(outline);
		const highlight = (element: Element | null) => {
			if (!visible(element)) { outline.style.display = "none"; return; }
			const box = element.getBoundingClientRect();
			Object.assign(outline.style, { display: "block", left: `${box.left - 4}px`, top: `${box.top - 4}px`, width: `${box.width + 8}px`, height: `${box.height + 8}px` });
			outline.dataset.demoHighlight = element.dataset.entityId ?? element.getAttribute("href") ?? stage;
		};
		setError(false);
		const navigation = DEMO_NAVIGATION_STEPS.includes(stage as DemoNavigationStep) ? stage as DemoNavigationStep : null;
		const started = navigation ? event({ event: "guided_substep_started", step: active, substep: navigation }) : Promise.resolve();
		void started.catch(() => { if (!stopped) setError(true); });
		const check = async () => {
			if (stopped || sending) return;
			const project = document.querySelector<HTMLElement>("[data-demo-project-id]");
			const node = target.nodeId ? document.querySelector(`.react-flow__node[data-id="${CSS.escape(target.nodeId)}"]`) : null;
			const field = inEditor ? (active === "shop" ? document.querySelector('[data-demo-field="shop-price"] input') : node?.querySelector(`[data-demo-field="${active}"]`)) ?? null : null;
			const form = inEditor && (active === "shop" ? field : document.querySelector(".react-flow"));
			const observation: DemoObservation = {
				path: location.pathname,
				projectId: project?.dataset.demoProjectId,
				entityId: inEditor ? editor?.entityId : undefined,
				view: inEditor ? editor?.view : undefined,
				nodeId: target.nodeId,
				visible: stage === "projects" ? visible(document.querySelector("[data-demo-project-list]")) : stage === "view" ? visible(form || null) : visible(active === "shop" ? field : node),
				expanded: active === "shop" ? document.activeElement === field : node?.querySelector('[data-demo-expanded="true"]') !== null && node !== null,
			};
			const editingStage = ["view", "node", "edit", "save"].includes(stage);
			setClosed(Boolean(["edit", "save"].includes(stage) && inEditor && editor?.view === "graph" && node && !node.querySelector('[data-demo-expanded="true"]')));
			setAway(editingStage && (!inEditor || (active !== "shop" && stage !== "view" && editor?.view !== "graph")));
			setMissing(Boolean(inEditor && (active === "shop" || editor?.view === "graph") && ((active !== "shop" && !node) || (active === "shop" && !field))));
			let focus: Element | null = null;
			const collectionLink = link(`${base}/${collection}`) ?? document.querySelector("[data-demo-navigation-toggle]");
			if ((stage === "projects" || stage === "project") && active !== "dialogue" && inProject) focus = collectionLink;
			else if (stage === "projects") focus = link("/dashboard");
			else if (stage === "project") focus = document.querySelector(`[data-demo-project-card="${CSS.escape(demo.projectId)}"]`) ?? link("/dashboard");
			else if (stage === "entity" || (editingStage && !inEditor)) focus = document.querySelector(`[data-entity-id="${CSS.escape(target.entityId)}"]`) ?? link(entityPath) ?? collectionLink ?? link("/dashboard");
			else if (stage === "view" || (editingStage && active !== "shop" && editor?.view !== "graph")) focus = active === "shop" ? field : document.querySelector("[data-demo-graph-tab]");
			else if (stage === "node") focus = active === "shop" ? field : node;
			else if (stage === "edit") focus = field ?? node;
			else if (stage === "save") focus = document.querySelector("[data-demo-save]");
			highlight(focus);
			if (navigation && matchesDemoObservation(navigation, observation, { entityId: target.entityId, nodeId: target.nodeId, projectId: demo.projectId, slug: demo.slug }, active)) {
				sending = true;
				try {
					await started;
					if (!stopped) await event({ event: "guided_substep_completed", step: active, substep: navigation, observation });
				} catch { if (!stopped) setError(true); }
			}
		};
		void check();
		const timer = window.setInterval(() => void check(), 350);
		return () => { stopped = true; window.clearInterval(timer); outline.remove(); };
	}, [active, stage, demo.projectId, demo.slug, target.entityId, target.nodeId, location.pathname, editor, inEditor, value, event, base, collection, entityPath, retry, inProject]);

	const number = [...DEMO_NAVIGATION_STEPS, "edit", "save", "reload", "use"].indexOf(stage) + 1;
	const enteringCollection = inProject && ((!inCollection && stage === "entity") || (active !== "dialogue" && (stage === "projects" || stage === "project")));
	return <section data-demo-substep={`${active}.${stage}`} aria-live="polite">
		{stage !== "completed" && <p className="text-xs text-violet-300">{t("hostedDemo.learning.position", { current: number, total: 9 })}</p>}
		<p className="my-3 text-sm">{t(stage === "completed" ? "hostedDemo.verified" : enteringCollection ? "hostedDemo.learning.openCollection" : `hostedDemo.learning.${active}.${stage}`, { name: progress.original?.name ?? t(`hostedDemo.steps.${active}.name`), collection: t(`nav.${collection}`), projects: t("nav.myProjects") })}</p>
		{away && <p className="mb-3 text-xs text-amber-200">{t("hostedDemo.learning.returnToEntity")}</p>}
		{closed && <p className="mb-3 text-xs text-amber-200">{t("hostedDemo.learning.reopenBlock")}</p>}
		{missing && <><p className="mb-2 text-xs text-amber-200">{t("hostedDemo.targetMissing")}</p><button className="text-sm text-violet-200" onClick={restore}>{t("hostedDemo.restore")}</button></>}
		{error && <p role="alert" className="text-sm text-amber-200">{t("hostedDemo.connectionError")} <button onClick={() => setRetry(value => value + 1)}>{t("hostedDemo.learning.retry")}</button></p>}
		{stage === "reload" && <button className="rounded-lg bg-violet-600 px-3 py-2 text-sm" onClick={refresh}>{t("hostedDemo.refresh")}</button>}
	</section>;
}
